import { type DropzoneOptions } from "react-dropzone";
import { collectDroppedFiles, collectSelectedFiles, DirectoryTraversalUnsupportedError } from "./directoryCollector";
import { type CollectedUploadFile } from "./types";

/**
 * What react-dropzone passes to `getFilesFromEvent`: a DOM drop/change event, or file handles when
 * `useFsAccessApi` is on (it is off here, so handles are ignored below).
 */
export type DropzoneFilesEvent = Parameters<NonNullable<DropzoneOptions["getFilesFromEvent"]>>[0];

export interface UploadCollectionOutcome {
  collectedFiles: readonly CollectedUploadFile[];
  error?: unknown;
}

export interface DropzoneEventCollection {
  dropzoneFiles: Array<File | DataTransferItem>;
  outcome?: UploadCollectionOutcome;
}

const outcomeFromCollection = (collectedFiles: readonly CollectedUploadFile[]): DropzoneEventCollection => ({
  dropzoneFiles: collectedFiles.map(file => file.file),
  outcome: { collectedFiles },
});

export const collectDropzoneEventFiles = async (event: DropzoneFilesEvent): Promise<DropzoneEventCollection> => {
  if (Array.isArray(event)) return { dropzoneFiles: [] };

  if ("dataTransfer" in event && event.dataTransfer) {
    const dataTransfer = event.dataTransfer as DataTransfer;
    const items = Array.from(dataTransfer.items ?? []).filter(item => item.kind === "file");
    if (event.type !== "drop") return { dropzoneFiles: items };

    try {
      return outcomeFromCollection(await collectDroppedFiles(dataTransfer));
    } catch (error) {
      const collectedFiles =
        error instanceof DirectoryTraversalUnsupportedError ? error.collectedFiles : ([] as CollectedUploadFile[]);
      return {
        dropzoneFiles: collectedFiles.map(file => file.file),
        outcome: { collectedFiles, error },
      };
    }
  }

  const selectedFiles =
    "target" in event && event.target && "files" in event.target
      ? Array.from((event.target as HTMLInputElement).files ?? [])
      : [];
  try {
    return outcomeFromCollection(collectSelectedFiles(selectedFiles));
  } catch (error) {
    return { dropzoneFiles: [], outcome: { collectedFiles: [], error } };
  }
};
